import React, { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { TaskExportSettings } from 'types/interfaces/TaskExportSettings';
import { transformVisibilityArrayToMap } from 'utils';

import { CustomTaskExportSettingsService } from 'components/services/CustomTaskExportSettingsService';
import { CustomTaskExportSetting } from 'components/services/interfaces/CustomTaskExportSetting';
import defaultExportSettings from 'constants/defaultExportSettings';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

interface ExportDialogProps {
  visible: boolean;
  onHide: () => void;
  onExport: (settings: Record<string, boolean>) => void;
}

function transformKeysToPascalCase<T extends Record<string, any>>(
  obj: T
): Record<string, any> {
  const newObj: Record<string, any> = {};

  for (const [key, value] of Object.entries(obj)) {
    const newKey = key.charAt(0).toUpperCase() + key.slice(1);
    newObj[newKey] = value;
  }
  return newObj;
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  visible,
  onHide,
  onExport
}) => {
  const toast = useRef<Toast>(null);
  const [exportSettings, setExportSettings] = useState<Record<string, boolean>>(
    defaultExportSettings
  );

  const [templateName, setTemplateName] = useState('');
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [isPersonalTemplate, setIsPersonalTemplate] = useState(false);

  const [personalTemplates, setPersonalTemplates] = useState<
    CustomTaskExportSetting[]
  >([]);
  const [sharedTemplates, setSharedTemplates] = useState<
    CustomTaskExportSetting[]
  >([]);
  const [activeTemplateId, setActiveTemplateId] = useState<number | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<number | null>(null);
  const elementUIs = useSelector((state: RootState) => state.auth.elementUIs);

  const visibilityClasses = transformVisibilityArrayToMap(elementUIs);

  // Загружаем при монтировании
  useEffect(() => {
    if (visible) {
      fetchPersonalTemplates();
      fetchSharedTemplates();
    }
  }, [visible]);

  const fetchPersonalTemplates = async () => {
    const response =
      await CustomTaskExportSettingsService.getPersonalTemplates();
    setPersonalTemplates(response.data);
  };

  const fetchSharedTemplates = async () => {
    const response = await CustomTaskExportSettingsService.getSharedTemplates();
    setSharedTemplates(response.data);
  };

  const saveTemplate = async () => {
    try {
      if (!templateName.trim()) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Название шаблона не может быть пустым',
          life: 3000
        });
        return;
      }
      const template: CustomTaskExportSetting = {
        ...exportSettings,
        name: templateName,
        hasUser: isPersonalTemplate, // <-- Правильное имя свойства
        id: undefined // Для нового шаблона не отправляем ID
      };

      const response =
        await CustomTaskExportSettingsService.createOrUpdateTemplate(template);

      // Проверяем ответ от сервера
      if (response.data === -1) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Шаблон с таким именем уже существует',
          life: 3000
        });
        return;
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Сохранено',
        detail: 'Шаблон успешно сохранен',
        life: 3000
      });

      fetchPersonalTemplates();
      fetchSharedTemplates();
      setShowSaveTemplateDialog(false);
      setTemplateName('');
    } catch (error) {
      console.error('Ошибка:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось сохранить шаблон'
      });
    }
  };

  const handleDeleteTemplate = async (id?: number) => {
    if (!id) return;
    setTemplateToDelete(id);
    setShowDeleteConfirmation(true);
  };

  const confirmDeleteTemplate = async () => {
    if (templateToDelete) {
      await CustomTaskExportSettingsService.deleteTemplate(templateToDelete);
      fetchPersonalTemplates();
      fetchSharedTemplates();
      setShowDeleteConfirmation(false);
      setTemplateToDelete(null);
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Шаблон удален',
        life: 3000
      });
    }
  };

  const applyTemplate = (template: CustomTaskExportSetting) => {
    if (activeTemplateId === template.id) {
      // Снимаем выбор шаблона
      setActiveTemplateId(null);
      setExportSettings(defaultExportSettings);
    } else {
      // применяем шаблон
      const { id, name, hasUser, userId, ...rest } = template;
      const pascalCaseRest = transformKeysToPascalCase(rest);
      setExportSettings(pascalCaseRest as Record<string, boolean>);
      setActiveTemplateId(id || null);
      setIsPersonalTemplate(hasUser);
    }
  };

  const toggleField = (field: keyof TaskExportSettings) => {
    if (activeTemplateId) {
      setActiveTemplateId(null);
    }
    setExportSettings((prev) => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleExport = () => {
    onExport(exportSettings);
    onHide();
  };

  const handleSaveTemplate = (isPersonal: boolean) => {
    setIsPersonalTemplate(isPersonal);
    setShowSaveTemplateDialog(true);
  };

  const exportOptions = [
    { key: 'HasIdColumn', label: '№ Наряда' },
    { key: 'HasExternalIdColumn', label: 'Внешний №' },
    { key: 'HasTaskNameColumn', label: 'Название наряда' },
    { key: 'HasTaskTypeColumn', label: 'Тип наряда' },
    { key: 'HasAssetNameColumn', label: 'Название объекта' },
    { key: 'HasAssetClassColumn', label: 'Тип объекта' },
    { key: 'HasAssetAddressColumn', label: 'Адрес объекта' },
    { key: 'HasAssetDescriptionColumn', label: 'Описание объекта' },
    { key: 'HasDepartmentColumn', label: 'Участок' },
    {
      key: 'HasEmployeesColumn',
      label: 'Исполнители',
      tooltip:
        'В случае, если исполнители не выбраны для выгрузки, в отчет попадет информация о работах в разрезе сущности "Наряд"'
    },
    { key: 'HasDescriptionColumn', label: 'Описание к наряду' },
    { key: 'HasStatusColumn', label: 'Статус наряда' },
    { key: 'HasClosingCodeColumn', label: 'Шифр закрытия наряда' },
    { key: 'HasCreatedColumn', label: 'Дата и время создания наряда' },
    { key: 'HasFactStartColumn', label: 'Факт. начало работы' },
    { key: 'HasStartColumn', label: 'План. начало работы' },
    { key: 'HasFinishColumn', label: 'План. завершение' },
    { key: 'HasFactFinishColumn', label: 'Факт. завершение' },
    { key: 'HasPriorityColumn', label: 'Приоритет' },
    { key: 'HasPlannedOperationColumns', label: 'Плановые работы' },
    { key: 'HasActualOperationColumns', label: 'Фактические работы' },
    { key: 'HasIntervalColumn', label: 'Интервал' }
  ];

  const allSelected = exportOptions.every(option => exportSettings[option.key]);
  const someSelected = exportOptions.some(option => exportSettings[option.key]);

  const selectAll = () => {
    if (activeTemplateId) {
      setActiveTemplateId(null);
    }
    const newSettings: Record<string, boolean> = {};
    exportOptions.forEach(option => {
      newSettings[option.key] = true;
    });
    setExportSettings(newSettings);
  };

  const clearAll = () => {
    if (activeTemplateId) {
      setActiveTemplateId(null);
    }
    const newSettings: Record<string, boolean> = {};
    exportOptions.forEach(option => {
      newSettings[option.key] = false;
    });
    setExportSettings(newSettings);
  };

  const isTemplateActive = (template: CustomTaskExportSetting) => {
    return activeTemplateId === template.id;
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        header="Настройки экспорта"
        visible={visible}
        resizable={false}
        style={{
          width: '54vw',
          minWidth: '1000px',
          height: '95vh',
          maxHeight: '920px'
        }}
        className={
          visibilityClasses['wholeui__roles'] ? 'whole-ui-role-active' : ''
        }
        onHide={onHide}
        pt={withDialogFooterSpacing()}
        footer={
          <div className="flex justify-content-between flex-wrap">
            <div className="flex">
              <Button
                label="Сохранить личный шаблон"
                icon="pi pi-save"
                onClick={() => handleSaveTemplate(true)}
              />
              <Button
                className="export-settings-btn__roles"
                label="Сохранить общий шаблон"
                icon="pi pi-save"
                onClick={() => handleSaveTemplate(false)}
              />
            </div>
            <div className="flex">
              <Button
                label="Экспорт"
                icon="pi pi-download"
                onClick={handleExport}
              />
              <Button
                label="Отмена"
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={onHide}
              />
            </div>
          </div>
        }
      >
        {/* Подсказки выше чекбоксов */}
        <Message
          text="Выберите необходимые столбцы для экспорта"
          severity="info"
          className="mb-2 mr-3"
        />
        <Message
          text="Экспорт будет учитывать фильтры, настроенные в таблице нарядов"
          severity="info"
          className="mb-2"
        />

        {/* Блок сохранённых шаблонов */}
        <div className="flex gap-4 mb-3 flex-wrap">
          {/* Личные шаблоны */}
          <div style={{ width: 'calc(50% - 10.5px)' }}>
            <h3>Личные шаблоны</h3>
            <div
              style={{
                width: '100%',
                maxHeight: '200px',
                overflow: 'auto'
              }}
              id="tags-scroll"
            >
              {personalTemplates.length > 0 ? (
                personalTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="flex align-items-center justify-content-between mb-2"
                    style={{
                      height: '42px',
                      cursor: 'pointer',
                      border: '2px solid',
                      borderRadius: '8px',
                      paddingLeft: '0.5rem',
                      backgroundColor: isTemplateActive(tpl)
                        ? 'rgb(225, 234, 245)'
                        : 'white',
                      borderColor: isTemplateActive(tpl)
                        ? 'rgb(225, 234, 245)'
                        : '#ccc'
                    }}
                    onClick={() => applyTemplate(tpl)}
                  >
                    <span>{tpl.name}</span>
                    <Button
                      icon="pi pi-trash"
                      style={{ height: '38px', width: '38px' }}
                      className="p-button-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(tpl.id);
                      }}
                      tooltip="Удалить шаблон"
                      tooltipOptions={{ position: 'top' }}
                    />
                  </div>
                ))
              ) : (
                <p>Нет личных шаблонов</p>
              )}
            </div>
          </div>

          {/* Общие шаблоны */}
          <div style={{ width: 'calc(50% - 10.5px)' }}>
            <h3>Общие шаблоны</h3>
            <div
              style={{
                width: '100%',
                maxHeight: '200px',
                overflow: 'auto'
              }}
              id="tags-scroll"
            >
              {sharedTemplates.length > 0 ? (
                sharedTemplates.map((tpl) => (
                  <div
                    key={tpl.id}
                    className="flex align-items-center justify-content-between mb-2"
                    style={{
                      height: '42px',
                      cursor: 'pointer',
                      border: isTemplateActive(tpl) ? undefined : '2px solid',
                      borderRadius: '8px',
                      paddingLeft: '0.5rem',
                      backgroundColor: isTemplateActive(tpl)
                        ? 'rgb(225, 234, 245)'
                        : 'white',
                      borderColor: isTemplateActive(tpl)
                        ? 'rgb(225, 234, 245)'
                        : '#ccc'
                    }}
                    onClick={() => applyTemplate(tpl)}
                  >
                    <span>{tpl.name}</span>
                    <Button
                      icon="pi pi-trash"
                      style={{ height: '38px', width: '38px' }}
                      size="small"
                      className="p-button-danger export-settings-btn__roles"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate(tpl.id);
                      }}
                      tooltip="Удалить шаблон"
                      tooltipOptions={{ position: 'top' }}
                    />
                  </div>
                ))
              ) : (
                <p>Нет общих шаблонов</p>
              )}
            </div>
          </div>
        </div>

        <Divider />
        <div className="flex gap-2 mb-3">
          <Button
            label="Выбрать все"
            icon="pi pi-check-square"
            onClick={selectAll}
            disabled={allSelected}
          />
          <Button
            label="Очистить"
            icon="pi pi-times-circle"
            onClick={clearAll}
            disabled={!someSelected}
            className="p-button-secondary"
          />
        </div>

        {/* Чекбоксы для настройки экспорта */}
        <div
          className="flex flex-wrap justify-content-start gap-2"
          style={{ display: 'flex' }}
        >
          {exportOptions.map(({ key, label, tooltip }) => (
            <div
              key={key}
              className="p-2 flex align-items-center"
              style={{
                flex: '1 1 calc(33.333% - 7px)',
                minWidth: 'calc(33.333% - 7px)',
                maxWidth: 'calc(33.333% - 7px)',
                height: '42px',
                marginBottom: '12px',
                border: '3px solid rgb(225, 234, 245)',
                borderRadius: '8px',
                alignItems: 'center'
              }}
            >
              <Checkbox
                inputId={key}
                checked={exportSettings[key]}
                onChange={() => toggleField(key as keyof TaskExportSettings)}
              />
              <label htmlFor={key} style={{ marginLeft: '0.5rem' }}>
                {label}
              </label>
              {tooltip && (
                <div style={{ height: 'fit-content', margin: 'auto 0' }}>
                  <i
                    id={`${key}-tooltip`}
                    className="pi pi-question-circle ml-2"
                    style={{ cursor: 'pointer' }}
                  />
                  <Tooltip target={`#${key}-tooltip`} content={tooltip} />
                </div>
              )}
            </div>
          ))}
        </div>
      </Dialog>

      {/* Диалог ввода имени шаблона */}
      <Dialog
        header="Сохранить шаблон"
        visible={showSaveTemplateDialog}
        style={{ width: '30vw' }}
        onHide={() => setShowSaveTemplateDialog(false)}
        pt={withDialogFooterSpacing()}
        footer={
          <div>
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={saveTemplate}
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => setShowSaveTemplateDialog(false)}
            />
          </div>
        }
      >
        <div className="p-fluid">
          <label htmlFor="templateName">Название шаблона</label>
          <InputText
            id="templateName"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            maxLength={50}
          />
        </div>
      </Dialog>

      <DeleteConfirmationDialog
        visible={showDeleteConfirmation}
        onHide={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmDeleteTemplate}
      >
        <p>Вы уверены, что хотите удалить этот шаблон?</p>
      </DeleteConfirmationDialog>
    </>
  );
};

export default ExportDialog;
